/**
 * <ConversationHeader> — à qui l'élève parle, en tête d'une conversation (molecule).
 *
 * Le retour, puis celui à qui l'on parle : sa capsule (ou sa tête), son nom, et une légende sous
 * le nom — ses matières pour un tuteur. Tout est calé à gauche : `<Header>` centre un titre
 * entre deux fentes, ce qui ne laisse pas de place à une personne.
 *
 * Elle ne dit ni présence ni « en train d'écrire » : hors live, la messagerie n'a ni l'une ni
 * l'autre aujourd'hui. Elle ne compte pas non plus les messages non lus : une fois le fil ouvert,
 * ils sont lus.
 *
 * La barre prend toute la largeur ; son contenu se borne à `maxContentWidth`, pour que le retour
 * tombe à l'aplomb du fil sur un grand écran. L'avatar arrive par une fente, comme dans le fil :
 * la capsule d'un tuteur (`<Avatar variant="tutor">`) ou la tête d'un champion.
 *
 * Dans une FEUILLE (la conversation avec Sam), c'est la même ligne, nue (`bare`) : la feuille a
 * déjà son fond, et sa croix se pose en bout de ligne (`end`).
 *
 * Composes DS pieces only — `<IconButton>`, `<Text>`, `<Row>`, `<Stack>`.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { IconButton } from "../../atoms/Buttons/IconButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { BOW_ROOM } from "../SessionRow/SessionRow"

export type ConversationHeaderProps = {
    /** « Camille R. » — une ligne, coupée si elle déborde. Vide : pas de nom (il se lit déjà dessous). */
    name: string
    /** Sous le nom : ses matières. Absente, le nom est seul. */
    caption?: string
    /** Sa capsule ou sa tête, `CONVERSATION_HEADER_AVATAR_SIZE` de côté. */
    avatar?: React.ReactNode
    /** Le retour. ABSENT = pas de flèche : la conversation tient dans un volet, à côté de sa liste. */
    onBack?: () => void
    /** La borne du contenu de la barre, en px — la colonne du fil. Absente, toute la largeur. */
    maxContentWidth?: number
    /** L'action de fin, en bout de ligne : la croix d'une feuille. Absente (défaut) : rien. */
    end?: React.ReactNode
    /**
     * La ligne d'une feuille, qui a déjà son fond : ni surface ni filet, serrée sous la poignée, et
     * le nœud de la tête pend dans son retrait bas. Défaut `false` : la barre d'une page.
     */
    bare?: boolean
    testID?: string
}

/** La capsule du livre, dans sa taille de liste : la même que sur la ligne du tuteur. */
export const CONVERSATION_HEADER_AVATAR_SIZE = 44

/** La cible d'un doigt. */
const BACK_SIZE = 44

/** La croix d'une feuille de conversation : sa cible, que l'appelant réserve avant qu'elle n'arrive. */
export const CONVERSATION_CLOSE_SIZE = 44

/** La croix qu'une feuille de conversation pose en bout de ligne (`end`). */
export function ConversationClose(props: { onPress?: () => void; accessibilityLabel: string }) {
    return (
        <IconButton
            iconName="times"
            size={CONVERSATION_CLOSE_SIZE}
            appearance="transparent"
            iconColor="text-muted"
            onPress={props.onPress}
            accessibilityLabel={props.accessibilityLabel}
        />
    )
}

const TEXTS = { back: "Retour" }

export function ConversationHeader(props: ConversationHeaderProps) {
    const { name, caption, avatar, onBack, maxContentWidth, end, bare = false, testID } = props
    const hasCaption = caption != null && caption !== ""

    return (
        <View style={bare ? undefined : styles.bar} testID={testID}>
            <Row gap="2" align="center" style={styles.content(maxContentWidth, onBack != null, end != null, bare)}>
                {onBack != null && (
                    <IconButton
                        iconName="chevron-left"
                        appearance="transparent"
                        iconColor="primary"
                        size={BACK_SIZE}
                        iconSize={20}
                        onPress={onBack}
                        accessibilityLabel={TEXTS.back}
                    />
                )}
                <Row gap="3" align="center" style={styles.who}>
                    {avatar != null && <View style={bare ? undefined : styles.avatar}>{avatar}</View>}
                    <Stack gap="0" style={styles.names}>
                        {name !== "" && (
                            <Text
                                size="text-lg"
                                weight="bold"
                                color={bare ? "text-primary" : "text-strong"}
                                numberOfLines={1}
                            >
                                {name}
                            </Text>
                        )}
                        {hasCaption && (
                            <Text size="text-sm" color="text-muted" numberOfLines={1}>
                                {caption}
                            </Text>
                        )}
                    </Stack>
                </Row>
                {end}
            </Row>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** La même barre que `<Header>` : la surface d'une carte, et un filet dessous. */
    bar: {
        backgroundColor: theme.colors["surface-raised"],
        borderColor: theme.colors.border,
        borderBottomWidth: 1,
    },
    content: (maxWidth: number | undefined, withBack: boolean, withEnd: boolean, bare: boolean) => ({
        width: "100%" as const,
        maxWidth,
        alignSelf: "center" as const,
        // Le retour porte déjà sa marge dans sa cible de 44 px ; l'action de fin aussi.
        paddingLeft: theme.spacing[withBack ? "1" : "4"],
        paddingRight: theme.spacing[withEnd ? "2" : "4"],
        paddingTop: theme.spacing[bare ? "1" : "2"],
        paddingBottom: theme.spacing["2"],
    }),
    who: { flex: 1, flexShrink: 1 },
    /** La place du nœud, qui pend sous la capsule. */
    avatar: { paddingBottom: BOW_ROOM },
    names: { flex: 1, flexShrink: 1 },
}))
